import { HashLocationStrategy, PlatformLocation } from '@angular/common';
import { Injectable, Optional } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { Inject } from '@angular/core';

/**
 * Hashbang routing: URLs look like  https://host/#!/route?query
 *
 * path() also accepts these incoming link formats so older email/SMS/EHR links keep working:
 *   /#!/bookAppointment?eh=TOKEN      (canonical)
 *   /#/bookAppointment?eh=TOKEN       (plain hash)
 *   /?eh=TOKEN#!/bookAppointment      (query before the hash)
 *   /bookAppointment?eh=TOKEN         (legacy path-style, pre-hashbang)
 * The router then rewrites the address bar to the canonical form, so a refresh
 * always requests "/" from the server and keeps the query string (e.g. ?eh=).
 */
@Injectable()
export class HashBangLocationStrategy extends HashLocationStrategy {

  constructor(
    _platformLocation: PlatformLocation,
    @Optional() @Inject(APP_BASE_HREF) _baseHref?: string
  ) {
    super(_platformLocation, _baseHref);
  }

  override prepareExternalUrl(internal: string): string {
    // super returns "#/path?query"; turn it into "<base>#!/path?query".
    // Prefixing the base href resets any legacy pathname/search, so a refresh
    // never asks the server for a route path it doesn't have.
    const url = super.prepareExternalUrl(internal);
    if (!url.startsWith('#')) return url;
    return this.docBaseHref + '#!' + url.substring(1);
  }

  override path(includeHash: boolean = false): string {
    const hash = this.platformLocation.hash || '';
    let route: string;

    if (hash.startsWith('#!')) {
      route = hash.substring(2);
    } else if (hash.startsWith('#/')) {
      route = hash.substring(1);
    } else {
      // No hash route — legacy path-style link, e.g. /bookAppointment?eh=...
      const base = this.docBaseHref;
      const pathname = this.platformLocation.pathname || '/';
      route = pathname.startsWith(base) ? '/' + pathname.substring(base.length) : pathname;
    }

    // Keep a query string that sits before the hash (/?eh=x#!/route) or on a legacy path link.
    const search = this.platformLocation.search || '';
    if (search && !route.includes('?')) {
      route += search;
    }

    return route || '/';
  }

  private get docBaseHref(): string {
    const base = this.platformLocation.getBaseHrefFromDOM() || '/';
    return base.endsWith('/') ? base : base + '/';
  }

  // Expose platformLocation for use in path()
  private get platformLocation(): PlatformLocation {
    return (this as any)._platformLocation as PlatformLocation;
  }
}
